import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Card,
  CardContent,
  Divider,
  Tab,
  Tabs
} from '@mui/material';
import { 
  BarChart as BarChartIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  PieChart as PieChartIcon
} from '@mui/icons-material';

import AuthLayout from '../components/AuthLayout';
import CostDashboard from '../components/CostDashboard';
import LoadingIndicator from '../components/LoadingIndicator';
import costService from '../services/cost.service';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`financial-tabpanel-${index}`}
      aria-labelledby={`financial-tab-${index}`}
      {...other}
      style={{ paddingTop: '16px' }}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `financial-tab-${index}`,
    'aria-controls': `financial-tabpanel-${index}`,
  };
};

const FinancialDashboard: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState({
    directTechnicalCosts: 'R$ 0,00',
    directTechnicalPercentage: '0%',
    totalCosts: 'R$ 0,00',
    commercialCosts: 'R$ 0,00',
    commercialPercentage: '0%',
    indirectCosts: 'R$ 0,00',
    indirectPercentage: '0%'
  });

  // Buscar dados financeiros ao carregar o componente
  useEffect(() => {
    fetchFinancialData();
  }, []);

  // Função para buscar e calcular os dados financeiros
  const fetchFinancialData = async () => {
    setLoading(true);
    try {
      // Buscar todos os custos
      const response = await costService.getCosts({ limit: 10000 });
      const costs = response.costs || [];
      
      // Calcular o total de custos (soma de todos os custos)
      const totalCostsAmount = costs.reduce((sum: number, cost: any) => sum + (cost.amount || 0), 0);
      
      // Calcular custos diretos técnicos (costType === 'technical_direct')
      const directTechnicalCostsAmount = costs
        .filter((cost: any) => cost.costType === 'technical_direct')
        .reduce((sum: number, cost: any) => sum + (cost.amount || 0), 0);
      
      // Calcular custos comerciais (costType === 'commercial_direct')
      const commercialCostsAmount = costs
        .filter((cost: any) => cost.costType === 'commercial_direct')
        .reduce((sum: number, cost: any) => sum + (cost.amount || 0), 0);
      
      // Calcular custos indiretos (costType === 'indirect')
      const indirectCostsAmount = costs
        .filter((cost: any) => cost.costType === 'indirect')
        .reduce((sum: number, cost: any) => sum + (cost.amount || 0), 0);
      
      // Calcular percentuais de representatividade em relação ao custo total
      const directTechnicalPercentage = totalCostsAmount > 0 
        ? (directTechnicalCostsAmount / totalCostsAmount) * 100 
        : 0;
      
      const commercialPercentage = totalCostsAmount > 0 
        ? (commercialCostsAmount / totalCostsAmount) * 100 
        : 0;
      
      const indirectPercentage = totalCostsAmount > 0 
        ? (indirectCostsAmount / totalCostsAmount) * 100 
        : 0;
      
      // Atualizar os dados de resumo
      setSummaryData({
        directTechnicalCosts: costService.formatCurrency(directTechnicalCostsAmount),
        directTechnicalPercentage: `${directTechnicalPercentage.toFixed(1)}%`,
        totalCosts: costService.formatCurrency(totalCostsAmount),
        commercialCosts: costService.formatCurrency(commercialCostsAmount),
        commercialPercentage: `${commercialPercentage.toFixed(1)}%`,
        indirectCosts: costService.formatCurrency(indirectCostsAmount),
        indirectPercentage: `${indirectPercentage.toFixed(1)}%`
      });
    } catch (error) {
      console.error('Erro ao buscar dados financeiros:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <AuthLayout title="Dashboard de Gestão Financeira">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h1">
          Dashboard de Gestão Financeira
        </Typography>
      </Box>

      {/* Cards de resumo */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MoneyIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Custo Direto Técnico
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {summaryData.directTechnicalCosts}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                {summaryData.directTechnicalPercentage} do total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Custo Comercial
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {summaryData.commercialCosts}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                {summaryData.commercialPercentage} do total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PieChartIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Custo Indireto
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {summaryData.indirectCosts}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                {summaryData.indirectPercentage} do total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BarChartIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Custos Totais
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {summaryData.totalCosts}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                100%a
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Abas para diferentes visualizações */}
      <Paper sx={{ width: '100%', mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="financial dashboard tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Análise de Custos" {...a11yProps(0)} />
            <Tab label="Receitas" {...a11yProps(1)} />
            <Tab label="Lucratividade" {...a11yProps(2)} />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <LoadingIndicator message="Carregando dados..." />
          ) : (
            <Box sx={{ p: 2 }}>
              <CostDashboard />
            </Box>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Análise de Receitas
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Esta seção está em desenvolvimento. Em breve, você poderá visualizar gráficos e análises detalhadas sobre as receitas da empresa.
            </Typography>
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Análise de Lucratividade
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Esta seção está em desenvolvimento. Em breve, você poderá visualizar gráficos e análises detalhadas sobre a lucratividade da empresa.
            </Typography>
          </Box>
        </TabPanel>
      </Paper>
    </AuthLayout>
  );
};

export default FinancialDashboard;
